import { Box, type SxProps, type Theme } from '@mui/material';
import FavoritesCheckbox, { type FavoritesCheckboxProps } from '../atoms/FavoritesCheckbox';
import {
  type FavoriteIconVisibility,
  SKIN_FEATURE,
  useFavoriteIconConfig,
  useSkinFeature,
} from '../../config/skinRegistry/configs';

type FavoriteVisibilityMode = 'always' | 'skin-config';

type FavoriteControlProps = Omit<FavoritesCheckboxProps, 'isChecked'> & {
  isChecked: boolean;
  canShow?: boolean;
  isHovered?: boolean;
  visibilityMode?: FavoriteVisibilityMode;
  containerSx?: SxProps<Theme>;
};

const shouldShowFavorite = ({
  canShow,
  isChecked,
  isHovered,
  configuredVisibility,
  visibilityMode,
}: {
  canShow: boolean;
  isChecked: boolean;
  isHovered: boolean;
  configuredVisibility: FavoriteIconVisibility;
  visibilityMode: FavoriteVisibilityMode;
}) => {
  if (!canShow) {
    return false;
  }

  if (visibilityMode === 'always' || configuredVisibility === 'always') {
    return true;
  }

  return isHovered || isChecked;
};

const FavoriteControl = ({
  isChecked,
  canShow = true,
  isHovered = false,
  visibilityMode = 'always',
  containerSx,
  ...checkboxProps
}: FavoriteControlProps) => {
  const favoritesEnabled = useSkinFeature(SKIN_FEATURE.FAVORITES);
  const { visibility } = useFavoriteIconConfig();
  const showFavorite = shouldShowFavorite({
    canShow: favoritesEnabled && canShow,
    isChecked,
    isHovered,
    configuredVisibility: visibility,
    visibilityMode,
  });

  if (!showFavorite) {
    return null;
  }

  const checkbox = <FavoritesCheckbox isChecked={isChecked} {...checkboxProps} />;

  if (containerSx) {
    return <Box sx={containerSx}>{checkbox}</Box>;
  }

  return checkbox;
};

export default FavoriteControl;
